import promotionsData from "./promotions.json";
import questsData from "./quests.json";
import questTargetsData from "./questTargets.json";
import questTargetAgeRangesData from "./questTargetAgeRanges.json";
import questTargetKeywordsData from "./questTargetKeywords.json";
import questTargetPrefecturesData from "./questTargetPrefectures.json";
import questTargetPurchaseChannelsData from "./questTargetPurchaseChannels.json";
import questTargetStoresData from "./questTargetStores.json";
import questTargetProductsData from "./questTargetProducts.json";
import questTargetCategoriesData from "./questTargetCategories.json";
import questTargetManufacturersData from "./questTargetManufacturers.json";
import questTargetSegmentsData from "./questTargetSegments.json";
import accountsData from "./accounts.json";
import domainsData from "./domains.json";
import regionsData from "./regions.json";
import prefecturesData from "./prefectures.json";
import purchaseChannelsData from "./purchaseChannels.json";
import storesData from "./stores.json";
import productsData from "./products.json";
import categoriesData from "./categories.json";
import manufacturersData from "./manufacturers.json";
import segmentsData from "./segments.json";
import auditLogsData from "./auditLogs.json";
import progressNotificationsData from "./progressNotifications.json";
import progressNotificationDaysData from "./progressNotificationDays.json";

import {
  PromotionStatusValue,
  QuestStatusValue,
  QuestCreateStatusValue,
  OwnerTypeValue,
  PromotionTypeValue,
  ProfileTypeValue,
  TemplateScopeValue,
  LinkParamValue,
  GenderValue,
  MaritalStatusValue,
  JoinConditionValue,
  IncentiveTypeValue,
  AuditLogOperationValue,
  DayOfWeekValue,
} from "@quest_editor/constants";

export type Promotion = {
  id: string;
  name: string;
  type: PromotionTypeValue;
  status: PromotionStatusValue;
  owner: OwnerTypeValue;
  description: string;
  options: {
    dataCheck: boolean;
    dataCheckPriority: boolean;
    profiles: ProfileTypeValue[];
    template: boolean;
    templateScope?: TemplateScopeValue;
  };
  returnUrl: string;
  hash?: string;
};

// progress_notifications テーブル
export type ProgressNotification = {
  id: string;
  promotionId: string;
  email: string;
  hasError?: boolean;
  errorMessage?: string | null;
};

// progress_notification_days 中間テーブル
export type ProgressNotificationDay = {
  id: string;
  progressNotificationId: string;
  day: DayOfWeekValue;
};

// GraphQLレスポンス用の展開された型
export type ProgressNotificationExpanded = {
  id: string;
  promotionId: string;
  email: string;
  days: DayOfWeekValue[];
  hasError?: boolean;
  errorMessage?: string | null;
};

export type Quest = {
  id: string;
  promotionId: string;
  name: string;
  status: QuestStatusValue;
  createStatus: QuestCreateStatusValue;
  progress: number | null;
  progressMax: number;
  description: string;
  incentiveType: IncentiveTypeValue;
  tamaruPoint: number | null;
  prizeName: string | null;
  reward: string;
  imageUrl: string;
  startAt: string | null;
  endAt: string | null;
  targetClearCount: number;
  estimateRequested: boolean;
  // QuestDetail用の追加フィールド（オプショナル）
  hash?: string;
  note?: string;
  popupImageUrl?: string;
  productCheckNotice?: string;
  checkerNote?: string;
  receiptCheckDescription?: string;
  checkerPhoneFile?: string;
  slackChannel?: string;
  memo?: string;
  linkTitle?: string;
  linkUrl?: string;
  linkParams?: LinkParamValue[];
  surveyId?: string | null;
  surveyPreInstruction?: string;
  qParam1Text?: string;
  qParam2Text?: string;
  qParam3Text?: string;
};

export type Account = {
  id: string;
  domainId: string;
  name: string;
  email: string;
};

export type Domain = {
  id: string;
  name: string;
  code: string;
};

export type Region = {
  id: string;
  order_val: number;
  name: string;
};

export type Prefecture = {
  id: string;
  order_val: number;
  regionId: string;
  display: boolean;
  name: string;
};

export type PurchaseChannel = {
  id: string;
  order_val: number;
  name: string;
};

export type RegionWithPrefectures = Region & {
  prefectures: Prefecture[];
};

export type Store = {
  id: string;
  name: string;
  phoneNumber: string;
  storeCount: number;
};

export type Product = {
  id: string;
  name: string;
  manufacturerId: string;
  barcode: string;
};

export type ProductWithManufacturer = Product & {
  manufacturer: Manufacturer;
};

export type Category = {
  id: string;
  major: string;
  middle: string;
  small: string;
  detail: string;
};

export type Manufacturer = {
  id: string;
  name: string;
};

export type Segment = {
  id: string;
  name: string;
  peopleCount: number;
};

export type AuditLog = {
  id: string;
  accountId: string;
  promotionId: string | null;
  operation: AuditLogOperationValue;
  datetime: string;
};

export type AuditLogExpanded = {
  id: string;
  account: Account;
  domain: Domain;
  promotion: Promotion | null;
  operation: AuditLogOperationValue;
  datetime: string;
};

// ======================
// QuestTarget関連の型定義（テーブル構造に対応）
// ======================

// quest_targets テーブル（単純カラムのみ）
export type QuestTarget = {
  id: string;
  questId: string;
  genders: GenderValue[];
  maritalStatuses: MaritalStatusValue[];
  elapsedDays: string;
  joinCondition: JoinConditionValue;
  purchaseDateFrom: string;
  purchaseDateTo: string;
  purchaseFrequency: number;
  clearedQuestId: string;
  unclearedQuestId: string;
  unparticipatedQuestId: string;
  divisions: number[];
  eligibleCount: number;
  deliveryNumber: number;
};

// quest_target_age_ranges テーブル
export type QuestTargetAgeRange = {
  id: string;
  questTargetId: string;
  lower: number;
  upper: number;
};

// quest_target_keywords テーブル
export type QuestTargetKeyword = {
  id: string;
  questTargetId: string;
  value: string;
};

// quest_target_prefectures 中間テーブル
export type QuestTargetPrefecture = {
  id: string;
  questTargetId: string;
  prefectureId: string;
};

// quest_target_purchase_channels 中間テーブル
export type QuestTargetPurchaseChannel = {
  id: string;
  questTargetId: string;
  purchaseChannelId: string;
};

// quest_target_stores 中間テーブル
export type QuestTargetStore = {
  id: string;
  questTargetId: string;
  storeId: string;
};

// quest_target_products 中間テーブル
export type QuestTargetProduct = {
  id: string;
  questTargetId: string;
  productId: string;
};

// quest_target_categories 中間テーブル
export type QuestTargetCategory = {
  id: string;
  questTargetId: string;
  categoryId: string;
};

// quest_target_manufacturers 中間テーブル
export type QuestTargetManufacturer = {
  id: string;
  questTargetId: string;
  manufacturerId: string;
};

// quest_target_segments 中間テーブル（target/exclude両方）
export type SegmentType = "target" | "exclude";
export type QuestTargetSegment = {
  id: string;
  questTargetId: string;
  segmentId: string;
  segmentType: SegmentType;
};

// GraphQLレスポンス用の展開された型
export type AgeRange = {
  id: string;
  lower: number | null;
  upper: number | null;
};

export type Keyword = {
  id: string;
  value: string;
};

export type QuestTargetExpanded = {
  id: string;
  questId: string;
  genders: GenderValue[];
  maritalStatuses: MaritalStatusValue[];
  elapsedDays: string;
  joinCondition: JoinConditionValue;
  purchaseDateFrom: string;
  purchaseDateTo: string;
  purchaseFrequency: number;
  clearedQuestId: string;
  unclearedQuestId: string;
  unparticipatedQuestId: string;
  divisions: number[];
  eligibleCount: number;
  deliveryNumber: number;
  // 関連テーブルから結合されたデータ
  ageRanges: AgeRange[];
  keywords: Keyword[];
  prefectures: Prefecture[];
  purchaseChannels: PurchaseChannel[];
  stores: Store[];
  products: ProductWithManufacturer[];
  categories: Category[];
  manufacturers: Manufacturer[];
  targetSegments: Segment[];
  excludeSegments: Segment[];
};

// ======================
// データのエクスポート
// ======================

export const promotions: Promotion[] = promotionsData.promotions as Promotion[];
export const quests: Quest[] = questsData.quests as Quest[];
export const questTargets: QuestTarget[] =
  questTargetsData.questTargets as QuestTarget[];
export const questTargetAgeRanges: QuestTargetAgeRange[] =
  questTargetAgeRangesData.questTargetAgeRanges;
export const questTargetKeywords: QuestTargetKeyword[] =
  questTargetKeywordsData.questTargetKeywords;
export const questTargetPrefectures: QuestTargetPrefecture[] =
  questTargetPrefecturesData.questTargetPrefectures;
export const questTargetPurchaseChannels: QuestTargetPurchaseChannel[] =
  questTargetPurchaseChannelsData.questTargetPurchaseChannels;
export const questTargetStores: QuestTargetStore[] =
  questTargetStoresData.questTargetStores;
export const questTargetProducts: QuestTargetProduct[] =
  questTargetProductsData.questTargetProducts;
export const questTargetCategories: QuestTargetCategory[] =
  questTargetCategoriesData.questTargetCategories;
export const questTargetManufacturers: QuestTargetManufacturer[] =
  questTargetManufacturersData.questTargetManufacturers;
export const questTargetSegments: QuestTargetSegment[] =
  questTargetSegmentsData.questTargetSegments as QuestTargetSegment[];
export const accounts: Account[] = accountsData.accounts;
export const domains: Domain[] = domainsData.domains;
export const regions: Region[] = regionsData.regions;
export const prefectures: Prefecture[] = prefecturesData.prefectures;
export const purchaseChannels: PurchaseChannel[] =
  purchaseChannelsData.purchaseChannels;
export const stores: Store[] = storesData.stores;
export const products: Product[] = productsData.products;
export const categories: Category[] = categoriesData.categories as Category[];
export const manufacturers: Manufacturer[] = manufacturersData.manufacturers;
export const segments: Segment[] = segmentsData.segments;
export const auditLogs: AuditLog[] = auditLogsData.auditLogs as AuditLog[];

// ======================
// ヘルパー関数
// ======================

export const getPromotionById = (id: string): Promotion | undefined => {
  return promotions.find((p) => p.id === id);
};

export const getQuestById = (id: string): Quest | undefined => {
  return quests.find((q) => q.id === id);
};

export const getQuestByHash = (hash: string): Quest | undefined => {
  return quests.find((q) => q.hash === hash);
};

export const getQuestsByPromotionId = (promotionId: string): Quest[] => {
  return quests.filter((q) => q.promotionId === promotionId);
};

export const getQuestBySurveyId = (surveyId: string): Quest | undefined => {
  return quests.find((q) => q.surveyId === surveyId);
};

export const getAccountById = (id: string): Account | undefined => {
  return accounts.find((a) => a.id === id);
};

export const getDomainById = (id: string): Domain | undefined => {
  return domains.find((d) => d.id === id);
};

export const getAccountsByDomainId = (domainId: string): Account[] => {
  return accounts.filter((a) => a.domainId === domainId);
};

export const getPrefecturesByRegionId = (regionId: string): Prefecture[] => {
  return prefectures.filter((p) => p.regionId === regionId);
};

export const regionsWithPrefectures: RegionWithPrefectures[] = regions.map(
  (r) => ({
    ...r,
    prefectures: prefectures.filter((p) => p.regionId === r.id),
  }),
);

export const getStoreById = (id: string): Store | undefined => {
  return stores.find((s) => s.id === id);
};

export const getProductById = (id: string): Product | undefined => {
  return products.find((p) => p.id === id);
};

export const getCategoryById = (id: string): Category | undefined => {
  return categories.find((c) => c.id === id);
};

export const getManufacturerById = (id: string): Manufacturer | undefined => {
  return manufacturers.find((m) => m.id === id);
};

export const getSegmentById = (id: string): Segment | undefined => {
  return segments.find((s) => s.id === id);
};

export const productsWithManufacturer: ProductWithManufacturer[] = products.map(
  (p) => ({
    ...p,
    manufacturer: getManufacturerById(p.manufacturerId) ?? {
      id: p.manufacturerId,
      name: "",
    },
  }),
);

// ======================
// QuestTarget関連のヘルパー関数
// ======================

export const getQuestTargetByQuestId = (
  questId: string,
): QuestTarget | undefined => {
  return questTargets.find((qt) => qt.questId === questId);
};

export const getQuestTargetExpandedByQuestId = (
  questId: string,
): QuestTargetExpanded | undefined => {
  const questTarget = getQuestTargetByQuestId(questId);
  if (!questTarget) return undefined;

  const questTargetId = questTarget.id;

  // 中間テーブルからデータを取得
  const ageRanges = questTargetAgeRanges
    .filter((ar) => ar.questTargetId === questTargetId)
    .map(({ id, lower, upper }) => ({ id, lower, upper }));

  const keywords = questTargetKeywords
    .filter((k) => k.questTargetId === questTargetId)
    .map(({ id, value }) => ({ id, value }));

  const prefectureList = questTargetPrefectures
    .filter((qtp) => qtp.questTargetId === questTargetId)
    .map((qtp) => prefectures.find((p) => p.id === qtp.prefectureId))
    .filter((p): p is Prefecture => p !== undefined);

  const purchaseChannelList = questTargetPurchaseChannels
    .filter((qtpc) => qtpc.questTargetId === questTargetId)
    .map((qtpc) =>
      purchaseChannels.find((pc) => pc.id === qtpc.purchaseChannelId),
    )
    .filter((pc): pc is PurchaseChannel => pc !== undefined);

  const storeList = questTargetStores
    .filter((qts) => qts.questTargetId === questTargetId)
    .map((qts) => stores.find((s) => s.id === qts.storeId))
    .filter((s): s is Store => s !== undefined);

  const productList = questTargetProducts
    .filter((qtp) => qtp.questTargetId === questTargetId)
    .map((qtp) => productsWithManufacturer.find((p) => p.id === qtp.productId))
    .filter((p): p is ProductWithManufacturer => p !== undefined);

  const categoryList = questTargetCategories
    .filter((qtc) => qtc.questTargetId === questTargetId)
    .map((qtc) => categories.find((c) => c.id === qtc.categoryId))
    .filter((c): c is Category => c !== undefined);

  const manufacturerList = questTargetManufacturers
    .filter((qtm) => qtm.questTargetId === questTargetId)
    .map((qtm) => manufacturers.find((m) => m.id === qtm.manufacturerId))
    .filter((m): m is Manufacturer => m !== undefined);

  const targetSegmentList = questTargetSegments
    .filter(
      (qts) =>
        qts.questTargetId === questTargetId && qts.segmentType === "target",
    )
    .map((qts) => segments.find((s) => s.id === qts.segmentId))
    .filter((s): s is Segment => s !== undefined);

  const excludeSegmentList = questTargetSegments
    .filter(
      (qts) =>
        qts.questTargetId === questTargetId && qts.segmentType === "exclude",
    )
    .map((qts) => segments.find((s) => s.id === qts.segmentId))
    .filter((s): s is Segment => s !== undefined);

  return {
    id: questTarget.id,
    questId: questTarget.questId,
    genders: questTarget.genders,
    maritalStatuses: questTarget.maritalStatuses,
    elapsedDays: questTarget.elapsedDays,
    joinCondition: questTarget.joinCondition,
    purchaseDateFrom: questTarget.purchaseDateFrom,
    purchaseDateTo: questTarget.purchaseDateTo,
    purchaseFrequency: questTarget.purchaseFrequency,
    clearedQuestId: questTarget.clearedQuestId,
    unclearedQuestId: questTarget.unclearedQuestId,
    unparticipatedQuestId: questTarget.unparticipatedQuestId,
    divisions: questTarget.divisions,
    eligibleCount: questTarget.eligibleCount,
    deliveryNumber: questTarget.deliveryNumber,
    ageRanges,
    keywords,
    prefectures: prefectureList,
    purchaseChannels: purchaseChannelList,
    stores: storeList,
    products: productList,
    categories: categoryList,
    manufacturers: manufacturerList,
    targetSegments: targetSegmentList,
    excludeSegments: excludeSegmentList,
  };
};

// ======================
// Preview用の型定義
// ======================

export type PreviewTargetProduct = {
  name: string;
  barcode: string;
};

export type PreviewQuest = {
  id: string;
  name: string;
  company: string;
  description: string;
  incentiveType: IncentiveTypeValue;
  tamaruPoint: number | null;
  prizeName: string | null;
  imageUrl: string;
  startAt: string | null;
  endAt: string | null;
  targetProducts?: PreviewTargetProduct[];
};

export type PhoneStatus = {
  time: string;
  battery: string;
};

// ======================
// AuditLog関連のヘルパー関数
// ======================

export const getAuditLogById = (id: string): AuditLog | undefined => {
  return auditLogs.find((al) => al.id === id);
};

export const getAuditLogsExpanded = (): AuditLogExpanded[] => {
  return auditLogs
    .map((al) => {
      const account = getAccountById(al.accountId);
      if (!account) return null;

      const domain = getDomainById(account.domainId);
      if (!domain) return null;

      const promotion = al.promotionId ? getPromotionById(al.promotionId) : null;

      return {
        id: al.id,
        account,
        domain,
        promotion: promotion ?? null,
        operation: al.operation,
        datetime: al.datetime,
      };
    })
    .filter((al): al is AuditLogExpanded => al !== null);
};

// ======================
// Progress Notifications
// ======================

export const progressNotifications: ProgressNotification[] =
  progressNotificationsData.progressNotifications as ProgressNotification[];

export const progressNotificationDays: ProgressNotificationDay[] =
  progressNotificationDaysData.progressNotificationDays as ProgressNotificationDay[];

export const getPromotionByHash = (hash: string): Promotion | undefined => {
  return promotions.find((p) => p.hash === hash);
};

export const getProgressNotificationsByPromotionId = (
  promotionId: string,
): ProgressNotificationExpanded[] => {
  const notifications = progressNotifications.filter(
    (pn) => pn.promotionId === promotionId,
  );

  return notifications.map((notification) => {
    const days = progressNotificationDays
      .filter((pnd) => pnd.progressNotificationId === notification.id)
      .map((pnd) => pnd.day);

    return {
      id: notification.id,
      promotionId: notification.promotionId,
      email: notification.email,
      days,
      hasError: notification.hasError,
      errorMessage: notification.errorMessage,
    };
  });
};
